import { useMemo, useState, type CSSProperties, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { Nis2Profile, Nis2Scope, Nis2ScopeCandidate } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import {
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatDay,
  Spinner,
  StatCard,
  StatusBadge,
} from '../common'
import { DataTable, type Column } from '../DataTable'

/**
 * NIS2 as a programme: who the entity is for ACN (the profile) and which
 * systems the directive applies to (the perimeter). Backend semantics live in
 * control-plane/app/services/nis2_perimetro_service.py.
 *
 * - The profile dates the obligations: the 9 months (incident notification)
 *   and the 18 months (base measures) run from the ACN communication of
 *   inclusion. Saving it re-seeds the dated obligations on the server.
 * - The perimeter is a set of rules (business services, networks, tags, CIs
 *   added or excluded by hand) so it follows the inventory. No rule = the
 *   whole inventory. The candidates answer «what did you leave out?».
 */

// ------------------------------------------------------------------ helpers

/** Date inputs are not in the base input rule of styles.css: same look here. */
const STILE_DATA: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 14,
  fontFamily: 'inherit',
}

/** An ISO day plus some months, clamped to the end of the month ('' if unreadable). */
function piuMesi(giorno: string, mesi: number): string {
  const [a, m, g] = giorno.split('-').map(Number)
  if (!a || !m || !g) return ''
  const d = new Date(Date.UTC(a, m - 1 + mesi, g))
  // 31 August + 18 months would roll over into March: stop at the month's last day.
  if (d.getUTCDate() !== g) d.setUTCDate(0)
  return d.toISOString().slice(0, 10)
}

function etichettaSoggetto(kind: string): string {
  switch (kind) {
    case 'essenziale': return t('Soggetto essenziale')
    case 'importante': return t('Soggetto importante')
    default: return t('Non ancora determinato')
  }
}

/** A text area into a list: one item per line (networks also accept commas and spaces). */
function righe(testo: string, separatori: RegExp = /\n+/): string[] {
  return [...new Set(testo.split(separatori).map((s) => s.trim()).filter(Boolean))]
}

function Valore({ v }: { v: string }) {
  return v ? <>{v}</> : <span className="text-muted">—</span>
}

// ------------------------------------------------------------------ profile

type BozzaProfilo = Omit<Nis2Profile, 'updated_by' | 'updated_at'>

function bozzaDa(p: Nis2Profile): BozzaProfilo {
  const { updated_by: _chi, updated_at: _quando, ...resto } = p
  void _chi
  void _quando
  return resto
}

/** Never saved and nothing that matters filled in. */
function profiloVuoto(p: Nis2Profile): boolean {
  return !p.updated_at && !p.entity_kind && !p.legal_name && !p.notified_at
}

/** The NIS2 profile: a summary by default, the form behind «Modifica». */
export function ProfiloNis2({ scrive }: { scrive: boolean }) {
  const profilo = useAsync(() => api.nis2Profile(), [])
  const [bozza, setBozza] = useState<BozzaProfilo | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [salvato, setSalvato] = useState(false)

  const p = profilo.data

  function apri() {
    if (!p) return
    setBozza(bozzaDa(p))
    setErrore(null)
    setSalvato(false)
  }

  async function salva(e: FormEvent) {
    e.preventDefault()
    if (!bozza) return
    setSalvando(true)
    setErrore(null)
    try {
      await api.saveNis2Profile(bozza)
      await profilo.reload()
      setBozza(null)
      setSalvato(true)
    } catch (err) {
      setErrore(err)
    } finally {
      setSalvando(false)
    }
  }

  const campo = (k: keyof BozzaProfilo) => (e: { target: { value: string } }) =>
    setBozza((b) => (b ? { ...b, [k]: e.target.value } : b))

  const azioni = scrive && p && !bozza && !profiloVuoto(p)
    ? <button type="button" className="secondary" onClick={apri}>{t('Modifica')}</button>
    : undefined

  return (
    <Card title={t('Profilo NIS2')} actions={azioni}>
      <ErrorBanner error={profilo.error} />
      {!p ? (
        profilo.loading ? <Spinner /> : null
      ) : bozza ? (
        <form onSubmit={salva}>
          <ErrorBanner error={errore} />
          <div className="form__sezione" style={{ marginTop: 0 }}>{t('Soggetto')}</div>
          <div className="row">
            <label className="field">
              <span>{t('Tipo di soggetto')}</span>
              <select value={bozza.entity_kind} onChange={campo('entity_kind')}>
                <option value="">{t('Non ancora determinato')}</option>
                <option value="essenziale">{t('Soggetto essenziale')}</option>
                <option value="importante">{t('Soggetto importante')}</option>
              </select>
            </label>
            <label className="field">
              <span>{t('Ragione sociale')}</span>
              <input type="text" value={bozza.legal_name} onChange={campo('legal_name')} maxLength={200} />
            </label>
          </div>
          <div className="row">
            <label className="field">
              <span>{t('Settore')}</span>
              <input type="text" value={bozza.sector} onChange={campo('sector')} maxLength={200}
                     placeholder={t('es. Sanità')} />
            </label>
            <label className="field">
              <span>{t('Sottosettore')}</span>
              <input type="text" value={bozza.subsector} onChange={campo('subsector')} maxLength={200} />
            </label>
            <label className="field">
              <span>{t('Riferimento della registrazione ACN')}</span>
              <input type="text" className="mono" value={bozza.registration_ref} onChange={campo('registration_ref')}
                     maxLength={100} />
            </label>
          </div>

          <div className="form__sezione">{t('Punto di contatto')}</div>
          <div className="row">
            <label className="field">
              <span>{t('Nome')}</span>
              <input type="text" value={bozza.contact_name} onChange={campo('contact_name')} maxLength={200} />
            </label>
            <label className="field">
              <span>{t('Email')}</span>
              <input type="email" value={bozza.contact_email} onChange={campo('contact_email')} maxLength={320} />
            </label>
            <label className="field">
              <span>{t('Telefono')}</span>
              <input type="text" inputMode="tel" autoComplete="tel" value={bozza.contact_phone}
                     onChange={campo('contact_phone')} maxLength={60} />
            </label>
          </div>
          <div className="row">
            <label className="field">
              <span>{t('Sostituto')}</span>
              <input type="text" value={bozza.substitute_name} onChange={campo('substitute_name')} maxLength={200} />
            </label>
            <label className="field">
              <span>{t('Email del sostituto')}</span>
              <input type="email" value={bozza.substitute_email} onChange={campo('substitute_email')} maxLength={320} />
            </label>
          </div>

          <div className="form__sezione">{t('Date')}</div>
          <div className="row">
            <label className="field">
              <span>{t('Comunicazione ACN di inclusione')}</span>
              <input type="date" style={STILE_DATA} value={bozza.notified_at} onChange={campo('notified_at')} />
              <p className="field__hint">
                {t('Da questa data decorrono i 9 mesi per la notifica degli incidenti e i 18 mesi per le misure di sicurezza di base.')}
              </p>
            </label>
            <label className="field">
              <span>{t('Approvazione dell’organo di gestione')}</span>
              <input type="date" style={STILE_DATA} value={bozza.management_approval_at}
                     onChange={campo('management_approval_at')} />
              <p className="field__hint">{t('Quando gli organi di amministrazione hanno approvato le misure di gestione del rischio.')}</p>
            </label>
          </div>

          <label className="field">
            <span>{t('Note')}</span>
            <textarea rows={3} value={bozza.notes} onChange={campo('notes')} maxLength={4000} />
          </label>

          <div className="checkbox">
            <input id="nis2-exposure-lookup" type="checkbox" checked={bozza.exposure_lookup}
                   onChange={(e) => setBozza({ ...bozza, exposure_lookup: e.target.checked })} />
            <label htmlFor="nis2-exposure-lookup">
              {t('Consulta Shodan InternetDB per gli IP pubblici')}
              <small>
                {t('Escono dalla piattaforma solo gli indirizzi pubblici del tenant, in sola lettura: nessuna scansione viene eseguita.')}
              </small>
            </label>
          </div>

          <div className="eagle-form__azioni">
            <button type="submit" disabled={salvando}>
              {salvando ? t('Salvo…') : t('Salva')}
            </button>
            <button type="button" className="secondary" disabled={salvando}
                    onClick={() => { setBozza(null); setErrore(null) }}>
              {t('Annulla')}
            </button>
          </div>
        </form>
      ) : (
        <SintesiProfilo p={p} scrive={scrive} salvato={salvato} onCompila={apri} />
      )}
    </Card>
  )
}

function SintesiProfilo({ p, scrive, salvato, onCompila }: {
  p: Nis2Profile
  scrive: boolean
  salvato: boolean
  onCompila: () => void
}) {
  if (profiloVuoto(p)) {
    return (
      <>
        <EmptyState
          title={t('Profilo non ancora compilato')}
          hint={t('Tipo di soggetto, punto di contatto e data della comunicazione ACN servono a datare gli obblighi NIS2.')}
        />
        {scrive && <button type="button" onClick={onCompila}>{t('Compila il profilo')}</button>}
      </>
    )
  }
  const notifica = p.notified_at ? piuMesi(p.notified_at, 9) : ''
  const misure = p.notified_at ? piuMesi(p.notified_at, 18) : ''
  return (
    <>
      {salvato && (
        <div className="banner banner--ok">
          {t('Profilo salvato: gli obblighi NIS2 sono datati a partire dalle date del profilo.')}
        </div>
      )}
      <dl className="contratto-dati">
        <div>
          <dt>{t('Tipo di soggetto')}</dt>
          <dd>
            {p.entity_kind
              ? <strong>{etichettaSoggetto(p.entity_kind)}</strong>
              : <span className="badge badge--warn">{etichettaSoggetto('')}</span>}
          </dd>
        </div>
        <div>
          <dt>{t('Ragione sociale')}</dt>
          <dd><Valore v={p.legal_name} /></dd>
        </div>
        <div>
          <dt>{t('Settore')}</dt>
          <dd><Valore v={[p.sector, p.subsector].filter(Boolean).join(' · ')} /></dd>
        </div>
        <div>
          <dt>{t('Registrazione ACN')}</dt>
          <dd className="mono"><Valore v={p.registration_ref} /></dd>
        </div>
        <div>
          <dt>{t('Punto di contatto')}</dt>
          <dd>
            <Valore v={p.contact_name} />
            {p.contact_email && <><br /><a href={`mailto:${p.contact_email}`}>{p.contact_email}</a></>}
            {p.contact_phone && <><br /><span className="mono">{p.contact_phone}</span></>}
          </dd>
        </div>
        <div>
          <dt>{t('Sostituto')}</dt>
          <dd>
            <Valore v={p.substitute_name} />
            {p.substitute_email && <><br /><a href={`mailto:${p.substitute_email}`}>{p.substitute_email}</a></>}
          </dd>
        </div>
        <div>
          <dt>{t('Comunicazione ACN di inclusione')}</dt>
          <dd>
            {p.notified_at ? formatDay(p.notified_at) : <span className="badge badge--warn">{t('non indicata')}</span>}
            {notifica && (
              <>
                <br />
                <small className="text-muted">{t('notifica degli incidenti dal {d}', { d: formatDay(notifica) })}</small>
                <br />
                <small className="text-muted">{t('misure di base entro il {d}', { d: formatDay(misure) })}</small>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>{t('Approvazione dell’organo di gestione')}</dt>
          <dd>
            {p.management_approval_at
              ? formatDay(p.management_approval_at)
              : <span className="text-muted">{t('non registrata')}</span>}
          </dd>
        </div>
        <div>
          <dt>{t('Shodan InternetDB')}</dt>
          <dd>
            {p.exposure_lookup
              ? <span className="badge badge--ok">{t('consultato')}</span>
              : <span className="badge badge--muted">{t('non consultato')}</span>}
          </dd>
        </div>
      </dl>
      {p.notes && (
        <p style={{ whiteSpace: 'pre-wrap', margin: '16px 0 0' }}>
          <small className="text-muted">{t('Note')}:</small> {p.notes}
        </p>
      )}
      {p.updated_at && (
        <p className="field__hint" style={{ marginTop: 16 }}>
          {t('Aggiornato da {chi} il {quando}', { chi: p.updated_by || '—', quando: formatDateTime(p.updated_at) })}
        </p>
      )}
    </>
  )
}

// ---------------------------------------------------------------- perimeter

type Membro = Nis2Scope['members'][number]

/** The NIS2 perimeter: rules, what they resolve to, and what they leave out. */
export function PerimetroNis2({ scrive, onCambiato }: { scrive: boolean; onCambiato: () => void }) {
  const perimetro = useAsync(() => api.nis2Scope(), [])
  const [modifica, setModifica] = useState(false)
  const [giro, setGiro] = useState(0)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  const s = perimetro.data

  /** After any change: the perimeter, the candidates and the report move together. */
  async function dopoCambio() {
    await perimetro.reload()
    setGiro((g) => g + 1)
    onCambiato()
  }

  async function escludi(id: string) {
    setInCorso(id)
    setErrore(null)
    try {
      await api.nis2ScopeExclude([id])
      await dopoCambio()
    } catch (err) {
      setErrore(err)
    } finally {
      setInCorso(null)
    }
  }

  const colonne: Column<Membro>[] = [
    {
      key: 'name',
      header: t('Sistema'),
      value: (m) => m.name ?? m.ci_id,
      render: (m) => <Link to={`/cis/${m.ci_id}`}>{m.name || m.ci_id.slice(0, 8)}</Link>,
    },
    { key: 'ci_type', header: t('Tipo'), mono: true },
    { key: 'ci_class', header: t('Classe'), hidden: true, value: (m) => m.ci_class ?? '' },
    {
      key: 'reasons',
      header: t('Perché è dentro'),
      value: (m) => m.reasons.join(', '),
      render: (m) => m.reasons.map((r) => <div key={r}><small>{r}</small></div>),
    },
  ]
  if (scrive) {
    colonne.push({
      key: 'azioni',
      header: '',
      sort: false,
      filter: false,
      searchable: false,
      render: (m) => (
        <button type="button" className="link" disabled={inCorso !== null}
                title={t('Toglie il sistema dal perimetro anche se una regola lo comprende')}
                onClick={() => void escludi(m.ci_id)}>
          {inCorso === m.ci_id ? t('Escludo…') : t('Escludi')}
        </button>
      ),
    })
  }

  const azioni = scrive && s && !modifica
    ? (
      <button type="button" className="secondary" onClick={() => setModifica(true)}>
        {s.defined ? t('Modifica le regole') : t('Definisci il perimetro')}
      </button>
    )
    : undefined

  return (
    <>
      <Card title={t('Perimetro NIS2')} actions={azioni}>
        <ErrorBanner error={perimetro.error} />
        <ErrorBanner error={errore} />
        {!s ? (
          perimetro.loading ? <Spinner /> : null
        ) : (
          <>
            {!s.defined && (
              <div className="banner banner--warn">
                <strong>{t('Perimetro non definito.')}</strong>{' '}
                {t('Senza regole il report misura l’intero inventario ({n} sistemi).', { n: s.count })}
              </div>
            )}
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('Il perimetro NIS2 comprende i sistemi informativi e di rete usati per le attività e i servizi comunicati ad ACN. Si scrive con regole (Business Service, reti, tag), così segue l’inventario invece di diventare una lista da aggiornare a mano.')}
            </p>

            {modifica ? (
              <EditorRegole
                s={s}
                onAnnulla={() => setModifica(false)}
                onSalvato={async () => {
                  setModifica(false)
                  await dopoCambio()
                }}
              />
            ) : (
              s.defined && <RegoleAttuali s={s} />
            )}

            <div className="stats" style={{ marginTop: 16 }}>
              <StatCard
                label={s.defined ? t('Sistemi nel perimetro') : t('Sistemi nell’inventario')}
                value={s.count}
                hint={s.defined ? undefined : t('nessuna regola: conta tutto')}
              />
            </div>
            {Object.keys(s.by_type).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {Object.entries(s.by_type).map(([tipo, n]) => (
                  <span key={tipo} className="chip"><span className="mono">{tipo}</span> <strong>{n}</strong></span>
                ))}
              </div>
            )}

            {s.defined && (
              <DataTable
                rows={s.members}
                columns={colonne}
                rowKey={(m) => m.ci_id}
                conteggio={{ nome: t('sistemi') }}
                emptyText={t('Le regole non comprendono ancora nessun sistema.')}
                nomeEsportazione={t('perimetro NIS2')}
                id="nis2-perimetro"
              />
            )}

            {s.rules.updated_at && (
              <p className="field__hint" style={{ marginTop: 12 }}>
                {t('Regole aggiornate da {chi} il {quando}', {
                  chi: s.rules.updated_by || '—',
                  quando: formatDateTime(s.rules.updated_at),
                })}
              </p>
            )}
          </>
        )}
      </Card>
      {s?.defined && <ProposteNis2 scrive={scrive} giro={giro} onCambiato={dopoCambio} />}
    </>
  )
}

/** The rules as they stand, read-only. */
function RegoleAttuali({ s }: { s: Nis2Scope }) {
  const r = s.rules
  const servizi = s.services.filter((x) => r.service_ids.includes(x.id))
  return (
    <dl className="kv" style={{ marginBottom: 8 }}>
      <dt>{t('Business Service')}</dt>
      <dd>
        {servizi.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          servizi.map((x) => (
            <span key={x.id} className="chip">
              <Link to={`/services/${x.id}`}>{x.name}</Link> · <StatusBadge status={x.criticality} />
            </span>
          ))
        )}
        {servizi.length > 0 && (
          <div>
            <small className="text-muted">
              {r.include_infrastructure
                ? t('con l’infrastruttura sotto i servizi')
                : t('senza l’infrastruttura sotto i servizi')}
            </small>
          </div>
        )}
      </dd>
      <dt>{t('Reti')}</dt>
      <dd>
        {r.networks.length === 0
          ? <span className="text-muted">—</span>
          : <div className="chips-dato">{r.networks.map((n) => <span key={n}>{n}</span>)}</div>}
      </dd>
      <dt>{t('Tag')}</dt>
      <dd>
        {r.tags.length === 0
          ? <span className="text-muted">—</span>
          : <div className="chips-dato">{r.tags.map((x) => <span key={x}>{x}</span>)}</div>}
      </dd>
      <dt>{t('A mano')}</dt>
      <dd>
        {t('{a} aggiunti, {e} esclusi', { a: r.ci_ids_in.length, e: r.ci_ids_out.length })}
      </dd>
    </dl>
  )
}

/** The rules editor. Saves only the rule fields it shows (and the resets asked for). */
function EditorRegole({ s, onAnnulla, onSalvato }: {
  s: Nis2Scope
  onAnnulla: () => void
  onSalvato: () => Promise<void>
}) {
  const r = s.rules
  const [servizi, setServizi] = useState<Set<string>>(() => new Set(r.service_ids))
  const [infrastruttura, setInfrastruttura] = useState(r.include_infrastructure)
  const [reti, setReti] = useState(r.networks.join('\n'))
  const [tag, setTag] = useState(r.tags.join('\n'))
  const [azzeraAggiunti, setAzzeraAggiunti] = useState(false)
  const [azzeraEsclusi, setAzzeraEsclusi] = useState(false)
  const [cerca, setCerca] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const elenco = useMemo(() => {
    const q = cerca.trim().toLowerCase()
    return q ? s.services.filter((x) => x.name.toLowerCase().includes(q)) : s.services
  }, [s.services, cerca])

  function alterna(id: string) {
    setServizi((prima) => {
      const dopo = new Set(prima)
      if (dopo.has(id)) dopo.delete(id)
      else dopo.add(id)
      return dopo
    })
  }

  async function salva(e: FormEvent) {
    e.preventDefault()
    setSalvando(true)
    setErrore(null)
    try {
      await api.saveNis2Scope({
        service_ids: [...servizi],
        include_infrastructure: infrastruttura,
        networks: righe(reti, /[\s,;]+/),
        tags: righe(tag),
        ...(azzeraAggiunti ? { ci_ids_in: [] } : {}),
        ...(azzeraEsclusi ? { ci_ids_out: [] } : {}),
      })
      await onSalvato()
    } catch (err) {
      setErrore(err)
      setSalvando(false)
    }
  }

  return (
    <form onSubmit={salva} style={{ marginBottom: 8 }}>
      <ErrorBanner error={errore} />
      <div className="form__sezione" style={{ marginTop: 0 }}>{t('Business Service')}</div>
      {s.services.length === 0 ? (
        <p className="field__hint">
          {t('Nessun Business Service definito.')} <Link to="/services">{t('Crealo dalla pagina dei servizi')}</Link>
        </p>
      ) : (
        <div className="field">
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('Entrano i punti di ingresso e i componenti di ogni servizio scelto ({n} scelti).', { n: servizi.size })}
          </p>
          {s.services.length > 10 && (
            <input type="search" value={cerca} onChange={(e) => setCerca(e.target.value)}
                   placeholder={t('Cerca un servizio')} style={{ maxWidth: 320, marginBottom: 8 }} />
          )}
          <div style={{
            maxHeight: 260, overflowY: 'auto', padding: '8px 12px',
            border: '1px solid var(--divider)', borderRadius: 'var(--radius-sm)',
          }}>
            {elenco.map((x) => (
              <div key={x.id} className="checkbox" style={{ marginBottom: 6 }}>
                <input id={`nis2-svc-${x.id}`} type="checkbox" checked={servizi.has(x.id)} onChange={() => alterna(x.id)} />
                <label htmlFor={`nis2-svc-${x.id}`}>
                  {x.name} <StatusBadge status={x.criticality} />
                </label>
              </div>
            ))}
            {elenco.length === 0 && <small className="text-muted">{t('Nessun servizio corrisponde.')}</small>}
          </div>
        </div>
      )}
      <div className="checkbox">
        <input id="nis2-infra" type="checkbox" checked={infrastruttura}
               onChange={(e) => setInfrastruttura(e.target.checked)} />
        <label htmlFor="nis2-infra">
          {t('Includi l’infrastruttura sotto i servizi')}
          <small>{t('Hypervisor, storage e apparati di rete su cui poggiano i componenti dei servizi scelti.')}</small>
        </label>
      </div>

      <div className="form__sezione">{t('Reti e tag')}</div>
      <div className="row">
        <label className="field">
          <span>{t('Reti')}</span>
          <textarea rows={4} className="mono" value={reti} onChange={(e) => setReti(e.target.value)}
                    placeholder={'10.10.0.0/16\n192.168.20.0/24'} />
          <p className="field__hint">{t('Una rete per riga, in notazione CIDR: entra ogni sistema con un indirizzo dentro.')}</p>
        </label>
        <label className="field">
          <span>{t('Tag')}</span>
          <textarea rows={4} className="mono" value={tag} onChange={(e) => setTag(e.target.value)}
                    placeholder={'nis2:si\nambiente:produzione'} />
          <p className="field__hint">{t('Un tag per riga, nella forma «chiave:valore»: entra ogni sistema che ne ha almeno uno.')}</p>
        </label>
      </div>

      <div className="form__sezione">{t('Sistemi aggiunti o esclusi a mano')}</div>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('{a} sistemi aggiunti a mano, {e} esclusi a mano. Un’esclusione vale sempre, anche se una regola comprende il sistema.', {
          a: r.ci_ids_in.length,
          e: r.ci_ids_out.length,
        })}
      </p>
      {r.ci_ids_in.length > 0 && (
        <div className="checkbox">
          <input id="nis2-reset-in" type="checkbox" checked={azzeraAggiunti}
                 onChange={(e) => setAzzeraAggiunti(e.target.checked)} />
          <label htmlFor="nis2-reset-in">{t('Togli i {n} sistemi aggiunti a mano', { n: r.ci_ids_in.length })}</label>
        </div>
      )}
      {r.ci_ids_out.length > 0 && (
        <div className="checkbox">
          <input id="nis2-reset-out" type="checkbox" checked={azzeraEsclusi}
                 onChange={(e) => setAzzeraEsclusi(e.target.checked)} />
          <label htmlFor="nis2-reset-out">
            {t('Annulla le {n} esclusioni a mano', { n: r.ci_ids_out.length })}
            <small>{t('I sistemi tornano nel perimetro se una regola li comprende, e tornano tra le proposte.')}</small>
          </label>
        </div>
      )}

      <div className="eagle-form__azioni" style={{ marginTop: 8 }}>
        <button type="submit" disabled={salvando}>
          {salvando ? t('Salvo…') : t('Salva le regole')}
        </button>
        <button type="button" className="secondary" disabled={salvando} onClick={onAnnulla}>
          {t('Annulla')}
        </button>
      </div>
    </form>
  )
}

// --------------------------------------------------------------- candidates

function etichettaProposta(kind: string): string {
  switch (kind) {
    case 'esposto': return t('esposto su Internet')
    case 'dipendenza': return t('dipendenza')
    case 'traffico': return t('traffico')
    case 'servizio comune': return t('servizio comune')
    default: return kind
  }
}

/** An exposed system out of the perimeter is the most serious gap: red. */
const CLASSE_PROPOSTA: Record<string, string> = {
  esposto: 'badge badge--error',
  dipendenza: 'badge badge--warn',
  traffico: 'badge badge--info',
  'servizio comune': 'badge badge--muted',
}

/** What the perimeter probably leaves out, each with the reason. */
function ProposteNis2({ scrive, giro, onCambiato }: {
  scrive: boolean
  giro: number
  onCambiato: () => Promise<void>
}) {
  const proposte = useAsync(() => api.nis2ScopeCandidates(), [giro])
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const elenco = proposte.data ?? []
  const esposti = elenco.filter((c) => c.kinds.includes('esposto')).length

  async function applica(azione: 'includi' | 'escludi') {
    const ids = [...scelti]
    if (ids.length === 0) return
    setLavoro(true)
    setErrore(null)
    try {
      if (azione === 'includi') await api.nis2ScopeInclude(ids)
      else await api.nis2ScopeExclude(ids)
      setScelti(new Set())
      await onCambiato()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  const colonne: Column<Nis2ScopeCandidate>[] = [
    {
      key: 'name',
      header: t('Sistema'),
      value: (c) => c.name ?? c.ci_id,
      render: (c) => <Link to={`/cis/${c.ci_id}`}>{c.name || c.ci_id.slice(0, 8)}</Link>,
    },
    { key: 'ci_type', header: t('Tipo'), mono: true },
    { key: 'ci_class', header: t('Classe'), hidden: true, value: (c) => c.ci_class ?? '' },
    {
      key: 'kinds',
      header: t('Perché proporlo'),
      value: (c) => c.kinds.map(etichettaProposta).join(', '),
      render: (c) => c.kinds.map((k) => (
        <div key={k}><span className={CLASSE_PROPOSTA[k] ?? 'badge badge--muted'}>{etichettaProposta(k)}</span></div>
      )),
    },
    {
      key: 'reasons',
      header: t('Dettaglio'),
      value: (c) => c.reasons.join(', '),
      render: (c) => c.reasons.map((r) => <div key={r}><small>{r}</small></div>),
    },
    { key: 'weight', header: t('Rilevanza'), type: 'number', hidden: true },
  ]

  return (
    <Card title={t('Proposte per allargare il perimetro')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Sistemi fuori dal perimetro che la piattaforma vede legati a quelli dentro: esposti su Internet, infrastruttura da cui dipendono, sistemi con cui parlano nel traffico degli ultimi 7 giorni, servizi comuni come controller di dominio e hypervisor.')}
      </p>
      <ErrorBanner error={proposte.error} />
      <ErrorBanner error={errore} />
      {!proposte.data ? (
        proposte.loading ? <Spinner /> : null
      ) : elenco.length === 0 ? (
        <EmptyState
          title={t('Nessuna proposta')}
          hint={t('La piattaforma non vede sistemi fuori dal perimetro legati a quelli dentro.')}
        />
      ) : (
        <>
          {esposti > 0 && (
            <div className="banner banner--error">
              {t('{n} sistemi esposti su Internet sono fuori dal perimetro: è la lacuna più grave, valuta di includerli.', { n: esposti })}
            </div>
          )}
          {scrive && scelti.size > 0 && (
            <div className="selezione-barra">
              <strong>{t('{n} selezionati', { n: scelti.size })}</strong>
              <button type="button" disabled={lavoro} onClick={() => void applica('includi')}>
                {t('Aggiungi al perimetro')}
              </button>
              <button type="button" className="secondary" disabled={lavoro} onClick={() => void applica('escludi')}
                      title={t('Esclude i sistemi dal perimetro: non saranno più proposti')}>
                {t('Non proporre più')}
              </button>
              <button type="button" className="link" disabled={lavoro} onClick={() => setScelti(new Set())}>
                {t('Annulla la selezione')}
              </button>
            </div>
          )}
          <DataTable
            rows={elenco}
            columns={colonne}
            rowKey={(c) => c.ci_id}
            conteggio={{ nome: t('sistemi') }}
            selezione={scrive ? { chiavi: scelti, onCambia: setScelti, id: (c) => c.ci_id } : undefined}
            isSelected={(c) => scelti.has(c.ci_id)}
            nomeEsportazione={t('proposte perimetro NIS2')}
            id="nis2-proposte"
          />
        </>
      )}
    </Card>
  )
}
